import { FC, ReactNode } from "react";
import { Link } from "react-router-dom";
import { clsx } from "clsx";
import { DataTable, DataTableColumn, DataTableEntityCell } from "../../../../design-system";
import { getColorFromLetter } from "../../../../common/Color";
import {
    dealStatusPresentation,
    StatusProgressCell,
} from "../../../project/components/ProjectStatusCell";
import { CategoryNode, DealNode, SpendView, TreeNode } from "../vendorSpend";
import { AverageScoreCell, SavingsCell, TotalValueCell } from "./SpendCells";
import classes from "./SpendTreeTable.module.less";

interface SpendTreeTableProps {
    /** The roots of the spend tree. */
    nodes: TreeNode[];
    /** What the roots are: categories or vendors. */
    view: SpendView;
    /** Where the tree rooted at a category is shown; the category's name links there. */
    categoryHref: (category: CategoryNode) => string;
    /** Controls first in the table's toolbar (the view switch). */
    emptyText?: ReactNode;
}

export const NO_CATEGORY_LABEL = "Not categorized";

/** The category's own name: the last label of its path. */
export const categoryName = (category: { path?: string[] }) =>
    category.path ? category.path[category.path.length - 1] : NO_CATEGORY_LABEL;

const nodeName = (node: TreeNode) =>
    node.kind === "category"
        ? categoryName(node)
        : node.kind === "vendor"
          ? node.vendor.name
          : node.deal.name || node.vendor.name;

const dealPath = (node: DealNode) =>
    node.deal.projectId ? `/project/${node.deal.projectId}` : `/order/${node.deal.id}`;

/** A row's name and, under it, where it sits or what state it is in. */
const NameCell: FC<{ name: ReactNode; detail?: ReactNode; className?: string }> = ({
    name,
    detail,
    className,
}) => (
    <span className={clsx(classes.node, className)}>
        <span className={classes.node__name}>{name}</span>
        {detail && <span className={classes.node__detail}>{detail}</span>}
    </span>
);

/**
 * The vendor page's spend tree: the categories, each tallying its subtree,
 * the vendors under the categories they have deals in, and their deals under
 * them.
 */
export const SpendTreeTable: FC<SpendTreeTableProps> = ({
    nodes,
    view,
    categoryHref,
    emptyText,
}) => {
    const columns: DataTableColumn<TreeNode>[] = [
        {
            key: "name",
            title: view === "category" ? "Category" : "Vendor",
            width: "20%",
            sort: "text",
            sortValue: (node) =>
                node.kind === "category" && !node.path ? undefined : nodeName(node),
            render: (node) => {
                if (node.kind === "category") {
                    // A narrow row: the name, then the tallies.
                    return (
                        <NameCell
                            className={classes["node--category"]}
                            name={<Link to={categoryHref(node)}>{categoryName(node)}</Link>}
                        />
                    );
                }
                if (node.kind === "vendor") {
                    const letter = (node.vendor.name || "?").charAt(0).toUpperCase();
                    return (
                        <DataTableEntityCell
                            size="small"
                            nameSize="large"
                            imageSrc={node.vendor.hero}
                            imageFallback={letter}
                            imageFallbackColor={getColorFromLetter(letter)}
                            name={
                                <Link to={`/vendor/${node.vendor.vendorKey}`}>
                                    {node.vendor.name}
                                </Link>
                            }
                        />
                    );
                }
                return (
                    <NameCell
                        className={classes["node--deal"]}
                        name={<Link to={dealPath(node)}>{nodeName(node)}</Link>}
                    />
                );
            },
        },
        {
            key: "deals",
            title: "Deals",
            sort: "number",
            sortValue: (node) => (node.kind === "deal" ? undefined : node.deals),
            render: (node) => (node.kind === "deal" ? null : node.deals),
        },
        {
            key: "status",
            title: "Status",
            // Pipeline order: not started, in negotiation, ready, signed.
            sort: "number",
            sortValue: (node) =>
                node.kind === "deal"
                    ? dealStatusPresentation(node.deal.negotiationStatus)?.stage
                    : undefined,
            render: (node) =>
                node.kind === "deal" ? (
                    <StatusProgressCell
                        status={dealStatusPresentation(node.deal.negotiationStatus)}
                    />
                ) : null,
        },
        {
            key: "score",
            title: "Score",
            sort: "number",
            sortValue: (node) => node.score,
            render: (node) => <AverageScoreCell tally={node} />,
        },
        {
            key: "value",
            title: "Baseline TCV",
            sort: "number",
            sortValue: (node) => node.value,
            render: (node) => <TotalValueCell tally={node} />,
        },
        {
            key: "savings",
            title: "Savings",
            sort: "number",
            sortValue: (node) => node.realizedSavings,
            render: (node) => (
                <SavingsCell tally={node} deal={node.kind === "deal" ? node.deal : undefined} />
            ),
        },
    ];

    return (
        <DataTable<TreeNode>
            columns={columns}
            rows={nodes}
            rowKey={(node) => node.key}
            childRows={(node) => (node.kind === "deal" ? undefined : node.children)}
            searchText={nodeName}
            searchPlaceholder={
                view === "category"
                    ? "Search categories, vendors and deals"
                    : "Search vendors and deals"
            }
            emptyText={emptyText}
            dense
        />
    );
};
